import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, Toolbar, ResponsiveTable, TableSkeleton, EmptyState, AutoBadge, ReasonModal, SecretRevealModal, Modal, Drawer, SectionCard, DateCell, StatusBadge, Pagination } from "@/admin/ui";
import { Package, Plus, Search, RefreshCw, Archive, Play, GitBranch, KeyRound, Undo2, AlertTriangle } from "lucide-react";

export default function AdminProductsV2() {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [drawer, setDrawer] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  const [secret, setSecret] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [newProduct, setNewProduct] = React.useState({ slug: "", name: "" });
  const perPage = 25;

  const load = React.useCallback(async () => {
    const params = { page, per_page: perPage };
    if (q) params.q = q;
    if (status) params.status = status;
    const r = await api.get("/api/admin/v2/products", { params });
    setItems(r.data.items); setTotal(r.data.total);
  }, [page, q, status]);
  React.useEffect(() => { load(); }, [load]);

  const doCreate = async () => {
    try {
      await api.post("/api/admin/v2/products", newProduct);
      toast.success(t("adminp.products.created"));
      setCreating(false); setNewProduct({ slug: "", name: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const openDrawer = async (id) => {
    const p = await api.get(`/api/admin/v2/products/${id}`).then((r) => r.data);
    const v = await api.get(`/api/admin/v2/products/${id}/versions`).then((r) => r.data);
    const s = await api.get(`/api/admin/v2/products/${id}/secrets`).then((r) => r.data);
    setDrawer({ p, versions: v.items, secrets: s.items });
  };
  const doAction = (kind, product) => {
    const config = {
      publish: { title: t("adminp.products.publish"), action: "publish", danger: false },
      archive: { title: t("adminp.products.archive"), action: "archive", danger: true },
      reactivate: { title: t("adminp.products.reactivate"), action: "reactivate", danger: false },
    }[kind];
    setModal({
      title: config.title, actionLabel: config.title, danger: config.danger,
      onSubmit: async (reason) => {
        await api.post(`/api/admin/v2/products/${product.id}/${config.action}`, { reason });
        toast.success(t("common.saved")); await load();
        if (drawer && drawer.p.id === product.id) openDrawer(product.id);
      },
    });
  };
  const doRotateSecret = (product) => {
    setModal({
      title: t("adminp.products.rotate_secret"), actionLabel: t("adminp.products.rotate"),
      extras: <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 text-amber-100 text-xs flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{t("adminp.products.rotate_warning")}
      </div>,
      onSubmit: async (reason) => {
        const r = await api.post(`/api/admin/v2/products/${product.id}/secrets/rotate`, { purpose: "default", reason });
        setSecret({
          value: r.data.secret_reveal_once,
          meta: `${product.slug} · purpose: ${r.data.purpose} · prefix ${r.data.secret_prefix}`,
          productId: product.id,
        });
      },
    });
  };
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const activeFilters = (q ? 1 : 0) + (status ? 1 : 0);

  return (
    <div className="space-y-6" data-testid="admin-products-v2">
      <PageHeader title={t("adminp.products.title")} description={t("adminp.products.subtitle")}
        primary={<button className="lp-btn-primary text-xs py-1.5 px-3" onClick={() => setCreating(true)} data-testid="pv2-new">
          <Plus className="w-3.5 h-3.5" /> {t("adminp.products.new")}
        </button>}
        secondary={<button className="lp-btn-ghost" onClick={load} data-testid="pv2-refresh" aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>} />

      <Toolbar right={<>
        {activeFilters > 0 && <button className="lp-btn-ghost" onClick={() => { setQ(""); setStatus(""); setPage(1); }}>Clear ({activeFilters})</button>}
        <div className="text-xs text-white/40">{total} {t("adminp.products.total")}</div>
      </>}>
        <div className="flex-1 relative min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input className="lp-input w-full pl-9 py-2 text-sm" placeholder={t("adminp.products.search_ph")}
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="pv2-search" />
        </div>
        <select className="lp-input text-xs py-1.5" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="pv2-status-filter">
          <option value="">{t("adminp.products.all_statuses")}</option>
          <option value="draft">draft</option>
          <option value="published">published</option>
          <option value="archived">archived</option>
        </select>
      </Toolbar>

      {!items ? <TableSkeleton rows={8} cols={5} /> : items.length === 0 ? (
        <EmptyState title={t("adminp.products.empty")} />
      ) : (
        <ResponsiveTable minWidth={900}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("adminp.products.col.name")}</th>
            <th>{t("adminp.products.col.slug")}</th>
            <th>{t("adminp.products.col.status")}</th>
            <th>{t("adminp.products.col.version")}</th>
            <th>{t("adminp.products.col.updated")}</th>
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="cursor-pointer hover:bg-white/5" onClick={() => openDrawer(p.id)} data-testid={`pv2-row-${p.id.slice(0, 6)}`}>
                <td className="text-white/90"><Package className="inline w-3.5 h-3.5 mr-1 text-white/40" />{p.name}</td>
                <td className="font-mono text-white/60 text-xs">{p.slug}</td>
                <td><AutoBadge value={p.status} testid={`pv2-status-${p.id.slice(0, 6)}`} /></td>
                <td className="text-white/70 tabular-nums">v{p.published_version || "—"} {p.draft_version_dirty && <StatusBadge tone="warning">dirty</StatusBadge>}</td>
                <td><DateCell value={p.updated_at} /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      {items && total > perPage && <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} unitLabel={t("adminp.products.total")} />}

      <Modal open={creating} onClose={() => setCreating(false)} title={t("adminp.products.new")}
        footer={<>
          <button className="lp-btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          <button className="lp-btn-primary" onClick={doCreate} data-testid="pv2-new-submit" disabled={!newProduct.slug || !newProduct.name}>{t("common.create")}</button>
        </>}>
        <label className="block space-y-1">
          <span className="text-xs text-white/60">slug</span>
          <input className="lp-input w-full" value={newProduct.slug} onChange={(e) => setNewProduct({ ...newProduct, slug: e.target.value })} data-testid="pv2-new-slug" data-autofocus />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-white/60">name</span>
          <input className="lp-input w-full" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} data-testid="pv2-new-name" />
        </label>
      </Modal>

      <Drawer open={!!drawer} onClose={() => setDrawer(null)} size="lg"
        subtitle={drawer?.p.slug} title={drawer?.p.name || ""} testid="pv2-drawer">
        {drawer && <>
          <div className="flex flex-wrap gap-2">
            <AutoBadge value={drawer.p.status} />
            {drawer.p.status !== "archived" && <button className="lp-btn-primary text-xs" onClick={() => doAction("publish", drawer.p)} data-testid="pv2-publish"><Play className="w-3.5 h-3.5" /> {t("adminp.products.publish")}</button>}
            {drawer.p.status !== "archived" && <button className="lp-btn-danger text-xs" onClick={() => doAction("archive", drawer.p)} data-testid="pv2-archive"><Archive className="w-3.5 h-3.5" /> {t("adminp.products.archive")}</button>}
            {drawer.p.status === "archived" && <button className="lp-btn-primary text-xs" onClick={() => doAction("reactivate", drawer.p)} data-testid="pv2-reactivate"><Undo2 className="w-3.5 h-3.5" /> {t("adminp.products.reactivate")}</button>}
            <button className="lp-btn-secondary text-xs" onClick={() => doRotateSecret(drawer.p)} data-testid="pv2-rotate"><KeyRound className="w-3.5 h-3.5" /> {t("adminp.products.rotate_secret")}</button>
          </div>
          <SectionCard title={<span className="flex items-center gap-1"><GitBranch className="w-3.5 h-3.5" /> {t("adminp.products.versions")}</span>}>
            <ul className="space-y-1 text-xs">
              {drawer.versions.map((v) => (
                <li key={v.id} className="flex justify-between border-t border-white/5 pt-1">
                  <span className="text-white/85">v{v.version} · <AutoBadge value={v.state} /></span>
                  <span className="text-white/40"><DateCell value={v.created_at} /></span>
                </li>
              ))}
              {drawer.versions.length === 0 && <li className="text-white/40 text-center">—</li>}
            </ul>
          </SectionCard>
          <SectionCard title={<span className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> {t("adminp.products.secrets")}</span>}>
            <ul className="space-y-1 text-xs">
              {drawer.secrets.map((s) => (
                <li key={s.id} className="flex justify-between border-t border-white/5 pt-1 gap-2">
                  <span className="text-white/85 font-mono truncate">{s.secret_prefix}… · {s.purpose}</span>
                  <StatusBadge tone={s.active ? "success" : "neutral"}>{s.active ? "active" : "revoked"}</StatusBadge>
                </li>
              ))}
              {drawer.secrets.length === 0 && <li className="text-white/40 text-center">{t("adminp.products.no_secrets")}</li>}
            </ul>
          </SectionCard>
        </>}
      </Drawer>

      <ReasonModal open={!!modal} {...(modal || {})} onClose={() => setModal(null)} onSubmit={modal?.onSubmit || (async () => {})} />
      <SecretRevealModal open={!!secret} secret={secret?.value || ""} meta={secret?.meta}
        onClose={() => { const pid = secret?.productId; setSecret(null); if (pid) openDrawer(pid); }} />
    </div>
  );
}
